// src/lib/fillup-anforderung.ts
// Modul 25: reine Logik der Anlege-Maske im Reiter „Kommissionieren".
// Bewusst ohne React, damit Grenzen und Idempotenz-Regel unabhaengig von der
// Oberflaeche geprueft werden koennen.

/** Untergrenze der Maske. `cartons: 0` ist im Backend das STORNO einer eigenen
 *  offenen Anforderung (types.ts fillupRequestSchema) — dieser Weg gehoert NICHT
 *  in die Anlege-Maske, dafuer gibt es den Stornieren-Knopf in der Liste. */
export const KARTONS_MIN = 1
/** Obergrenze aus dem Backend-Schema (`z.number().int().min(0).max(999)`). */
export const KARTONS_MAX = 999

export function klemmeKartons(wert: number): number {
  if (!Number.isFinite(wert)) return KARTONS_MIN
  return Math.min(KARTONS_MAX, Math.max(KARTONS_MIN, Math.trunc(wert)))
}

/** Merkt sich fuer die Dauer einer Maskensitzung, welche Idempotenz-Id zu welchem
 *  Vorgang gehoert. Schluessel ist Nummer + Menge, Wert die UUID. */
export type Vorgangsspeicher = Map<string, string>

export function neuerVorgangsspeicher(): Vorgangsspeicher {
  return new Map()
}

/** Baut den Schluessel eines Vorgangs. Das Trennzeichen `|` kommt in keiner
 *  Sternnummer vor — ohne echtes Trennzeichen waeren ("12", 34) und ("123", 4)
 *  derselbe Schluessel.
 *
 *  Der Schluessel muss den VORGANG so genau beschreiben, wie das Backend ihn
 *  unterscheidet: dort ist die `request_id` der Anker fuer „schon gebucht".
 *  Zwei verschiedene Schreibvorgaenge duerfen sich also nie denselben Schluessel
 *  teilen — sonst haelt das Backend den zweiten faelschlich fuer eine Wiederholung
 *  des ersten und tut nichts (Gemini-Review 2026-08-04, Befund C). Deshalb bilden
 *  die Aufrufer den GESENDETEN Inhalt ab, nicht bloss das Ziel. */
export function vorgangsSchluessel(...teile: Array<string | number>): string {
  return teile.join('|')
}

/** Die Idempotenz-Id haengt am VORGANG (Nummer + Menge), nicht am Klick:
 *  - gleicher Vorgang (Doppelklick, Wiederholung nach Netzfehler) -> dieselbe Id,
 *    das Backend erkennt die Wiederholung und legt nichts doppelt an;
 *  - geaenderte Nummer oder Menge -> NEUE Id, sonst wuerde das Backend die
 *    Aenderung als Wiederholung abtun und die neue Menge verschlucken.
 *
 *  Bewusst ein Speicher statt eines einzelnen Merkers (Gemini-Review 2026-08-04):
 *  scheitert Artikel A mit unklarem Ausgang, laeuft dazwischen Artikel B und kehrt
 *  der Bediener dann zu A zurueck, muss A wieder SEINE alte Id bekommen — sonst
 *  bucht das Backend wegen `mode:'add'` ein zweites Mal obendrauf. */
export function idFuerVorgang(
  speicher: Vorgangsspeicher,
  schluessel: string,
  neueId: () => string,
): string {
  const vorhanden = speicher.get(schluessel)
  if (vorhanden !== undefined) return vorhanden
  const id = neueId()
  speicher.set(schluessel, id)
  return id
}

/** Nach einem eindeutigen Ausgang (angelegt, erhoeht, vorgemerkt, Wiederholung
 *  erkannt) ist der Vorgang erledigt. Er wird freigegeben, damit eine BEWUSSTE
 *  Wiederholung derselben Bestellung eine neue Id bekommt und nicht als Duplikat
 *  verschluckt wird. */
export function vorgangAbschliessen(
  speicher: Vorgangsspeicher,
  schluessel: string,
): void {
  speicher.delete(schluessel)
}

// ------------------------------------------------------------
// Antwort-Deutung (Modul 25; seit Modul 26 von zwei Stellen genutzt — dem
// Anlege-Dialog und dem Mengen-Steller an der Listenzeile).
// ------------------------------------------------------------
import type { FillupAnforderungErgebnis } from '@/api/warehouse'

export type Meldung = { art: 'ok' | 'hinweis' | 'fehler'; text: string }

/** Uebersetzt die Antwortformen des Backends in eine Meldung fuer den Bediener.
 *  `blocked` kommt als ACK mit HTTP 200 — es ist eine fachliche Ablehnung, kein
 *  Transportfehler (routes.ts Z.1297–1302). */
export function deuteErgebnis(
  ergebnis: FillupAnforderungErgebnis,
  gesendeteKartons: number,
): Meldung {
  if ('blocked' in ergebnis) return { art: 'fehler', text: ergebnis.message }
  if (!('request_db_id' in ergebnis)) {
    return { art: 'hinweis', text: 'Bereits angelegt — es wurde nichts doppelt erzeugt.' }
  }
  if (ergebnis.duplicate) {
    return { art: 'hinweis', text: 'Bereits angelegt — es wurde nichts doppelt erzeugt.' }
  }
  if (ergebnis.status === 'storniert') {
    return { art: 'hinweis', text: 'Die offene Anforderung wurde storniert.' }
  }
  if (ergebnis.request_kind === 'pallet') return {
    art: ergebnis.vorgemerkt ? 'hinweis' : 'ok',
    text: `1 ganze Palette · ${palletSourceText(ergebnis.source_lager)} · Ziel Lager ${ergebnis.target_lager ?? 2}${ergebnis.vorgemerkt ? ' · Vorgemerkt' : ' · Auftrag angelegt'}`,
  }
  // Modul 26: eine Senkung kann die Anforderung ERLEDIGEN — es wurde bereits genug
  // geholt. Diese Pruefung MUSS vor `updated` stehen, sonst meldet der Fall
  // faelschlich „Menge steht jetzt auf N" und der Bediener sucht die Zeile weiter.
  if (ergebnis.status === 'erledigt') {
    return {
      art: 'hinweis',
      text: 'Nichts mehr zu holen — es wurde schon genug gepickt, die Anforderung ist erledigt.',
    }
  }
  if (ergebnis.updated) {
    const gesamt = ergebnis.cartons_requested ?? gesendeteKartons
    return { art: 'ok', text: `Menge steht jetzt auf ${gesamt} Karton(s).` }
  }
  if (ergebnis.vorgemerkt) {
    return {
      art: 'hinweis',
      text: `Vorgemerkt — am Platz liegt noch nichts (${gesendeteKartons} Karton(s)).`,
    }
  }
  return { art: 'ok', text: `Auftrag angelegt: ${gesendeteKartons} Karton(s).` }
}

/** Whole-pallet requests have no cartons/set amount, and every intentional request has its own UUID. */
export function palletRequestBody(code:string,requestId:string,rollLength?:number){return {code,request_id:requestId,request_kind:'pallet' as const,source_lager:2 as const,...(rollLength===undefined?{}:{roll_length_m:rollLength})}}
export function palletSourceText(source: 1 | 2 | null | undefined) {
  return source == null ? 'Quelle ausstehend' : `Quelle Lager ${source}`
}
export function palletTargetLager(row: { source_lager?: 1 | 2; target_lager?: 1 | 2; return_required?: boolean }) {
  return row.return_required ? row.source_lager ?? 2 : row.target_lager ?? 2
}
